import { useEffect, useState } from 'react'
import { NavLink } from 'react-router'
import { FiArrowRight } from 'react-icons/fi'
import { Container, Reveal, HoverText } from '@/components/common'
import { ROUTES } from '@/constants/routes'
import { slides, SLIDE_INTERVAL } from '@/constants/hero'

export const Hero = () => {
  const [active, setActive] = useState(0)

  useEffect(() => {
    const id = globalThis.setInterval(() => {
      setActive((current) => (current + 1) % slides.length)
    }, SLIDE_INTERVAL)
    return () => globalThis.clearInterval(id)
  }, [])

  return (
    <section className="relative isolate flex min-h-screen items-center overflow-hidden bg-ibon-dark">
      {/* Full-bleed crossfading background slideshow */}
      {slides.map((slide, index) => (
        <div
          key={slide.src}
          aria-hidden={index !== active}
          className={`absolute inset-0 -z-20 bg-cover bg-center transition-opacity duration-1000 ease-in-out ${
            index === active ? 'opacity-100' : 'opacity-0'
          }`}
          style={{ backgroundImage: `url(${slide.src})` }}
        />
      ))}
      <div className="absolute inset-0 -z-10 bg-ibon-dark/75" />
      <div className="absolute inset-0 -z-10 bg-linear-to-b from-ibon-dark/40 via-transparent to-ibon-dark" />

      <Container className="flex flex-col items-center py-32 text-center">
        <Reveal variant="fade" duration={650}>
          <span className="text-xs font-semibold uppercase tracking-[0.3em] text-white/70">
            International Development &middot; Africa &amp; the Caribbean
          </span>
        </Reveal>

        <Reveal variant="up" delay={120} duration={800}>
          <h1 className="mt-8 max-w-5xl text-4xl font-bold leading-[1.08] tracking-[-0.02em] text-white sm:text-5xl lg:text-6xl xl:text-7xl">
            Connecting Africa and the Caribbean
          </h1>
        </Reveal>

        <Reveal variant="up" delay={200} duration={800}>
          <p className="mt-5 max-w-3xl text-xl font-medium leading-snug tracking-[-0.01em] text-white/35 sm:text-2xl lg:text-3xl">
            Through Entrepreneurship, Education, and Culture
          </p>
        </Reveal>

        <Reveal variant="up" delay={280} duration={800}>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-white/65 sm:text-lg">
            IBON World is an international non-profit organisation that connects young people,
            entrepreneurs, institutions, and communities across Africa and the Caribbean. We help
            people build skills, expand networks, and access opportunities that improve lives and
            strengthen communities.
          </p>
        </Reveal>

        <Reveal variant="up" delay={360} duration={800}>
          <div className="mt-11 flex flex-col items-center gap-4 sm:flex-row sm:flex-wrap sm:justify-center">
            <NavLink
              to={ROUTES.PROGRAMMES}
              className="group inline-flex items-center gap-3 rounded-full bg-ibon-sky px-9 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-ibon-dark transition-colors hover:bg-[#6fcdef]"
            >
              <HoverText text="Explore Our Programmes" />
              <FiArrowRight
                className="transition-transform duration-300 group-hover:translate-x-1"
                size={16}
              />
            </NavLink>
            <NavLink
              to={ROUTES.PARTNER}
              className="group inline-flex items-center gap-3 rounded-full border border-white/30 px-9 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:border-ibon-sky hover:text-ibon-sky"
            >
              <HoverText text="Partner With Us" />
              <FiArrowRight
                className="transition-transform duration-300 group-hover:translate-x-1"
                size={16}
              />
            </NavLink>
            <NavLink
              to={ROUTES.DONATE}
              className="group inline-flex items-center gap-3 px-9 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-white/70 transition-colors hover:text-white"
            >
              <HoverText text="Donate Now" />
              <FiArrowRight
                className="transition-transform duration-300 group-hover:translate-x-1"
                size={16}
              />
            </NavLink>
          </div>
        </Reveal>
      </Container>

      {/* Slide indicators */}
      <div className="absolute bottom-8 left-1/2 z-10 flex -translate-x-1/2 items-center gap-3">
        {slides.map((slide, index) => (
          <button
            key={slide.src}
            type="button"
            onClick={() => setActive(index)}
            aria-label={`Show slide ${index + 1}`}
            aria-current={index === active}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              index === active ? 'w-8 bg-ibon-sky' : 'w-4 bg-white/40 hover:bg-white/70'
            }`}
          />
        ))}
      </div>
    </section>
  )
}
